"use client";
import type { Locale } from "@commerce-frontend/i18n/types";
import type { ProductDetailPageFragmentFragment as ProductDetailPageFragment } from "@commerce-frontend/types";
import { Button } from "@commerce-frontend/ui/base/Button";
import { Icon } from "@commerce-frontend/ui/base/Icon/Icon";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogPortal,
	DialogTitle,
	DialogTrigger,
} from "@commerce-frontend/ui/components/Dialog/Dialog";
import { isValue } from "@labdigital/toolkit";
import { XIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { RenderBlock } from "~/components/cms/RenderBlock/RenderBlock";

type Props = {
	className?: string;
	sizeGuide: ProductDetailPageFragment["sizeGuide"];
	locale: Locale;
};

export const ProductSizeGuideDialog = ({ className, sizeGuide, locale }: Props) => {
	const t = useTranslations("ProductSizeGuideDialog");

	if (!sizeGuide) {
		return null;
	}

	return (
		<>
			<Dialog>
				<DialogTrigger asChild>
					<Button variant="linkColor" aria-label={t("open-dialog")} className={className}>
						<Icon icon="ruler" className="size-5 bg-brand" />
						{t("button-label")}
					</Button>
				</DialogTrigger>

				<DialogPortal>
					<DialogContent
						onOpenAutoFocus={(e) => {
							// prevent focus to the close button
							e.preventDefault();
						}}
						className="flex flex-col gap-0"
						aria-label={t("accessibility-name")}
						side="responsive"
					>
						<DialogTitle>{t("accessibility-name")}</DialogTitle>
						<div className="flex flex-row px-3xl pt-3xl">
							<div className="shadow-xs flex size-10 items-center justify-center rounded-md border border-gray-200">
								<Icon icon="ruler" className="size-5 bg-gray-700" />
							</div>
							<div className="pl-4">
								<h4 className="text-xl font-semibold">{t("title")}</h4>
								<span className="text-sm text-gray-600">{t("subtitle")}</span>
							</div>
							<DialogClose className="ml-auto self-start" asChild>
								<Button
									className="focus:ring-accent-level-2 relative top-[-10px] size-11 rounded-xs text-gray-400 hover:bg-gray-50 hover:text-gray-500 focus:bg-gray-50"
									icon={<XIcon />}
									variant="link"
									aria-label={t("close-dialog")}
								/>
							</DialogClose>
						</div>
						<div className="custom-scrollbar flex w-full grow flex-col overflow-y-scroll">
							<div className="flex flex-col gap-xl p-3xl">
								{sizeGuide?.filter(isValue).map((block) => {
									if (!("__typename" in block)) {
										return null;
									}

									return (
										<RenderBlock key={block.id} block={block} locale={locale} wrapContent={false} />
									);
								})}
							</div>
						</div>
					</DialogContent>
				</DialogPortal>
			</Dialog>
		</>
	);
};
